/**
 * Shared caregiver data hooks. All scoping goes through repository methods
 * (contract §14.1): linked clients come from repositories.caregiver, and
 * per-client medication data is always fetched with the client id scoped
 * through the repository — never through caller-trusted input.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { repositories } from '@/services/repositories'
import { isAppError } from '@/lib/supabase/errors'
import type { AppError, MedicationIntakeEvent, MedicationOccurrence, Profile } from '@/types'
import type { LoadStatus } from '@/features/medication'

function toErrorState(error: unknown): { status: 'unavailable' | 'error'; error: AppError | null } {
  if (isAppError(error)) {
    if (error.code === 'SUPABASE_NOT_CONFIGURED') return { status: 'unavailable', error: null }
    return { status: 'error', error }
  }
  return {
    status: 'error',
    error: {
      code: 'UNKNOWN',
      userMessage: 'Er ging iets mis. Probeer het opnieuw.',
      retryable: true,
      source: 'database',
    },
  }
}

export function useLinkedClients() {
  const [state, setState] = useState<{ status: LoadStatus; clients: Profile[]; error: AppError | null }>({
    status: 'loading',
    clients: [],
    error: null,
  })

  const load = useCallback(async () => {
    setState((prev) => ({ ...prev, status: 'loading', error: null }))
    try {
      const clients = await repositories.caregiver.listLinkedClients()
      setState({ status: 'ready', clients, error: null })
    } catch (error) {
      const next = toErrorState(error)
      setState({ status: next.status, clients: [], error: next.error })
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  return useMemo(() => ({ ...state, refresh: load }), [state, load])
}

export type ClientDaySummary = {
  clientId: string
  dueCount: number
  missedCount: number
  takenCount: number
  pendingProofCount: number
  recentEvents: MedicationIntakeEvent[]
  failed: boolean
}

export type CaregiverDashboardData = {
  status: LoadStatus
  error: AppError | null
  clients: Profile[]
  summaries: Map<string, ClientDaySummary>
  /** Recent intake events across all linked clients, newest first. */
  recentEvents: { event: MedicationIntakeEvent; client: Profile | null }[]
  /** Intake events with proof awaiting caregiver review, oldest first. */
  pendingProofs: { event: MedicationIntakeEvent; client: Profile | null }[]
  unreadNotifications: number
  refresh: () => Promise<void>
}

function summarizeOccurrences(occurrences: MedicationOccurrence[]): Pick<ClientDaySummary, 'dueCount' | 'missedCount' | 'takenCount'> {
  let dueCount = 0
  let missedCount = 0
  let takenCount = 0
  for (const occurrence of occurrences) {
    if (occurrence.status === 'due' || occurrence.status === 'planned') dueCount += 1
    else if (occurrence.status === 'missed') missedCount += 1
    else if (
      occurrence.status === 'taken' ||
      occurrence.status === 'proof_uploading' ||
      occurrence.status === 'proof_uploaded' ||
      occurrence.status === 'proof_viewed'
    ) {
      takenCount += 1
    }
  }
  return { dueCount, missedCount, takenCount }
}

/**
 * Dashboard aggregation (spec §9.3). Each client's data fails independently
 * so one failing client never blanks the whole dashboard.
 */
export function useCaregiverDashboardData(date: string): CaregiverDashboardData {
  const [state, setState] = useState<Omit<CaregiverDashboardData, 'refresh'>>({
    status: 'loading',
    error: null,
    clients: [],
    summaries: new Map(),
    recentEvents: [],
    pendingProofs: [],
    unreadNotifications: 0,
  })

  const load = useCallback(async () => {
    setState((prev) => ({ ...prev, status: 'loading', error: null }))
    try {
      const clients = await repositories.caregiver.listLinkedClients()
      const unreadNotifications = await repositories.notifications
        .unreadCount()
        .catch(() => 0)

      const perClient = await Promise.all(
        clients.map(async (client): Promise<ClientDaySummary> => {
          try {
            const [occurrences, events] = await Promise.all([
              repositories.medication.listOccurrences(date, date, client.id),
              repositories.medication.listIntakeEvents(client.id, 20),
            ])
            const pendingProof = events.filter(
              (event) => event.proofUploadedAt !== null && event.caregiverViewedAt === null,
            )
            return {
              clientId: client.id,
              ...summarizeOccurrences(occurrences),
              pendingProofCount: pendingProof.length,
              recentEvents: events.slice(0, 5),
              failed: false,
            }
          } catch {
            return {
              clientId: client.id,
              dueCount: 0,
              missedCount: 0,
              takenCount: 0,
              pendingProofCount: 0,
              recentEvents: [],
              failed: true,
            }
          }
        }),
      )

      const clientById = new Map(clients.map((client) => [client.id, client]))
      const summaries = new Map(perClient.map((summary) => [summary.clientId, summary]))

      const recentEvents = perClient
        .flatMap((summary) =>
          summary.recentEvents.map((event) => ({
            event,
            client: clientById.get(event.clientId) ?? null,
          })),
        )
        .sort((a, b) => b.event.updatedAt.localeCompare(a.event.updatedAt))
        .slice(0, 8)

      const pendingProofs = perClient
        .flatMap((summary) =>
          summary.recentEvents
            .filter((event) => event.proofUploadedAt !== null && event.caregiverViewedAt === null)
            .map((event) => ({ event, client: clientById.get(event.clientId) ?? null })),
        )
        .sort((a, b) =>
          (a.event.proofUploadedAt ?? '').localeCompare(b.event.proofUploadedAt ?? ''),
        )

      setState({
        status: 'ready',
        error: null,
        clients,
        summaries,
        recentEvents,
        pendingProofs,
        unreadNotifications,
      })
    } catch (error) {
      const next = toErrorState(error)
      setState({
        status: next.status,
        error: next.error,
        clients: [],
        summaries: new Map(),
        recentEvents: [],
        pendingProofs: [],
        unreadNotifications: 0,
      })
    }
  }, [date])

  useEffect(() => {
    void load()
  }, [load])

  return useMemo(() => ({ ...state, refresh: load }), [state, load])
}
